import { useCallback, useMemo, useState } from 'react';
import { Modal } from '@/shared/ui/Modal/Modal';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { Loader } from '@/shared/ui/Loader/Loader';
import { useError } from '@/shared/lib/hooks/useError';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { formatDateTimeForDisplay } from '@/shared/lib/utils/dateFormat';
import { formatPrivateId } from '@/shared/lib/utils/formatPrivateId';
import { DateInput } from '@/shared/ui/DateInput/DateInput';
import { normalizeApiError } from '@/shared/lib/utils/apiError';
import { isActiveAccount } from '@/shared/lib/utils/accountPresentation';
import classNames from 'classnames';
import {
  useGetBalanceQuery,
  useDepositBalanceMutation,
  useWithdrawBalanceMutation,
  useGetClubTimeSpentQuery,
  useGetClubDetailsQuery,
  type BalanceOperationItem,
  CLUB_ROLE_LABELS,
  type ClubRole,
} from '@/entities/Club';
import s from './ClubTimeModal.module.scss';

interface Props {
  clubId: number;
  clubName: string;
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

type Tab = 'operations' | 'usage';

const PAGE_SIZE = 50;
const MAX_TECHNOCOINS = 10_000_000;
const MAX_TECHNOCOINS_DIGITS = 8;

const dateMaskToIsoStartOfDay = (mask: string): string | null => {
  if (!mask || mask.length !== 10) return null;
  const [dd, mm, yyyy] = mask.split('.');
  if (!dd || !mm || !yyyy) return null;
  if (dd.length !== 2 || mm.length !== 2 || yyyy.length !== 4) return null;
  return `${yyyy}-${mm}-${dd}T00:00:00Z`;
};

const formatTechnocoins = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return amount.toLocaleString('ru-RU');
};

const operationTitle = (op: BalanceOperationItem): string => {
  return op.type === 'deposit' ? 'Пополнение' : 'Списание';
};

const parsePositiveInt = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return Math.floor(n);
};

export const ClubTimeModal = ({ clubId, clubName, open, onClose, readOnly = false }: Props) => {
  const onAlert = useAlert();
  const { data: clubDetails } = useGetClubDetailsQuery(clubId, { skip: !open });
  const isReadOnly = readOnly || !isActiveAccount(clubDetails?.accountStatus);
  const [tab, setTab] = useState<Tab>('operations');

  const {
    data: balanceResp,
    isFetching: balanceFetching,
    isLoading: balanceLoading,
    error: balanceError,
    refetch: refetchBalance,
  } = useGetBalanceQuery(clubId, { skip: !open });

  const [usageOffset, setUsageOffset] = useState(0);
  const {
    data: usageResp,
    isFetching: usageFetching,
    isLoading: usageLoading,
    error: usageError,
  } = useGetClubTimeSpentQuery({ clubId, limit: PAGE_SIZE, offset: usageOffset }, { skip: !open || tab !== 'usage' });

  const [depositBalance, { isLoading: depositing, error: depositError }] = useDepositBalanceMutation();
  const [withdrawBalance, { isLoading: withdrawing, error: withdrawError }] = useWithdrawBalanceMutation();

  useError(balanceError);
  useError(usageError);
  useError(depositError);
  useError(withdrawError);

  const [depositTechnocoins, setDepositTechnocoins] = useState('');
  const [depositRubles, setDepositRubles] = useState('');
  const [expiresAtMask, setExpiresAtMask] = useState('');
  const [depositReason, setDepositReason] = useState('');

  const [withdrawTechnocoins, setWithdrawTechnocoins] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  const [addMode, setAddMode] = useState<'technocoins' | 'unlimited'>('technocoins');

  const currentBalance = balanceResp?.balanceTechnocoins ?? 0;
  const currentLimit = balanceResp?.balanceLimit ?? 0;

  const depositErrorText = useMemo(() => {
    if (addMode !== 'technocoins') return '';
    if (depositTechnocoins === '') return '';

    const amount = parsePositiveInt(depositTechnocoins);
    if (amount === null) return '';
    if (amount > MAX_TECHNOCOINS) return `Слишком большое значение. Максимум: ${MAX_TECHNOCOINS} Ŧ`;

    return '';
  }, [addMode, depositTechnocoins]);

  const withdrawErrorText = useMemo(() => {
    if (withdrawTechnocoins === '') return '';

    const amount = parsePositiveInt(withdrawTechnocoins);
    if (amount === null) return '';
    if (amount > MAX_TECHNOCOINS) return `Слишком большое значение. Максимум: ${MAX_TECHNOCOINS} Ŧ`;
    if (amount > currentBalance) {
      return `Недостаточно средств. Максимум для списания: ${formatTechnocoins(Math.max(0, currentBalance))} Ŧ`;
    }

    return '';
  }, [withdrawTechnocoins, currentBalance]);

  const canDeposit = useMemo(() => {
    if (!depositReason.trim()) return false;
    if (addMode === 'technocoins') {
      const n = Number(depositTechnocoins);
      return Number.isFinite(n) && n > 0 && !depositErrorText;
    }
    return Boolean(dateMaskToIsoStartOfDay(expiresAtMask));
  }, [addMode, depositTechnocoins, expiresAtMask, depositReason, depositErrorText]);

  const canWithdraw = useMemo(() => {
    if (!withdrawReason.trim()) return false;
    const n = Number(withdrawTechnocoins);
    return Number.isFinite(n) && n > 0 && !withdrawErrorText;
  }, [withdrawTechnocoins, withdrawReason, withdrawErrorText]);

  const headerInfo = useMemo(() => {
    const unlimited = balanceResp?.unlimitedUntil ?? null;
    const unlimitedStr = unlimited ? formatDateTimeForDisplay(unlimited) : null;
    const nearLimit = currentLimit > 0 && currentBalance <= currentLimit;
    return {
      balance: formatTechnocoins(currentBalance),
      balanceLimit: currentLimit > 0 ? formatTechnocoins(currentLimit) : null,
      unlimitedUntil: unlimitedStr,
      nearLimit,
    };
  }, [balanceResp, currentBalance, currentLimit]);

  const operations = balanceResp?.items ?? [];
  const usageItems = usageResp?.items ?? [];

  const hasMoreUsage = Boolean(usageResp && usageItems.length < (usageResp.total ?? 0));

  const onDeposit = useCallback(async () => {
    if (isReadOnly) return;
    const technocoins = parsePositiveInt(depositTechnocoins) ?? 0;
    const expiresAtIso = dateMaskToIsoStartOfDay(expiresAtMask);

    try {
      await depositBalance({
        clubId,
        reason: depositReason.trim(),
        technocoins: addMode === 'technocoins' ? technocoins : 0,
        expiresAt: addMode === 'unlimited' ? expiresAtIso : null,
        amountRubles: depositRubles !== '' ? (parsePositiveInt(depositRubles) ?? 0) : null,
      }).unwrap();
    } catch (e) {
      const n = normalizeApiError(e);
      if (n?.code === 'INVALID_REQUEST') {
        onAlert('Пополнение не удалось. Проверьте введённые данные.', true);
        return;
      }
      onAlert('Пополнение не удалось. Проверьте введённые данные.', true);
      return;
    }

    setDepositTechnocoins('');
    setDepositRubles('');
    setExpiresAtMask('');
    setDepositReason('');
    await refetchBalance();
  }, [
    depositBalance,
    clubId,
    addMode,
    depositReason,
    depositTechnocoins,
    expiresAtMask,
    refetchBalance,
    onAlert,
    isReadOnly,
  ]);

  const onWithdraw = useCallback(async () => {
    if (isReadOnly) return;
    const technocoins = parsePositiveInt(withdrawTechnocoins) ?? 0;

    try {
      await withdrawBalance({
        clubId,
        technocoins,
        reason: withdrawReason.trim(),
      }).unwrap();
    } catch (e) {
      const n = normalizeApiError(e);
      if (n?.code === 'INVALID_REQUEST') {
        onAlert(
          `Списание не удалось. Максимум для списания: ${formatTechnocoins(Math.max(0, currentBalance))} Ŧ.`,
          true,
        );
        return;
      }
      onAlert('Списание не удалось. Проверьте введённые данные.', true);
      return;
    }

    setWithdrawTechnocoins('');
    setWithdrawReason('');
    await refetchBalance();
  }, [
    withdrawBalance,
    clubId,
    withdrawTechnocoins,
    withdrawReason,
    refetchBalance,
    onAlert,
    currentBalance,
    isReadOnly,
  ]);

  const isLoading = tab === 'operations' ? balanceLoading : usageLoading;
  const isFetching = tab === 'operations' ? balanceFetching : usageFetching;

  return (
    <Modal open={open} onClose={onClose} title={`Баланс клуба: ${clubName}`} titleSize="h2" contentClassName={s.wide}>
      <div className={s.root}>
        <div className={s.header}>
          <div className={s.balance}>
            Баланс: <strong>{headerInfo.balance} Ŧ</strong>
            {headerInfo.nearLimit && <span className={s.warning}> (низкий баланс)</span>}
            {headerInfo.balanceLimit && <span className={s.muted}> (лимит: {headerInfo.balanceLimit} Ŧ)</span>}
            {headerInfo.unlimitedUntil && <span className={s.muted}> (безлимит до {headerInfo.unlimitedUntil})</span>}
          </div>
        </div>

        <div className={s.toggleRow}>
          <div className={s.toggleGroup}>
            <div className={s.switchTabs}>
              <button
                type="button"
                className={classNames(s.switchTab, { [s.active]: tab === 'operations' })}
                onClick={() => {
                  setTab('operations');
                }}
              >
                Пополнения
              </button>
              <button
                type="button"
                className={classNames(s.switchTab, { [s.active]: tab === 'usage' })}
                onClick={() => {
                  setTab('usage');
                  setUsageOffset(0);
                }}
              >
                Сессии
              </button>
            </div>
          </div>
        </div>

        {tab === 'operations' && (
          <>
            {!isReadOnly && <div className={classNames(s.sectionTitle, s.sectionTitleCentered)}>Пополнить баланс</div>}

            {!isReadOnly && (
              <div className={s.formRow}>
                {addMode === 'technocoins' ? (
                  <Input
                    className={s.inputField}
                    label=""
                    error={depositErrorText}
                    value={depositTechnocoins}
                    onChange={(e) => {
                      const next = typeof e === 'string' ? e : e.target.value;
                      if (next === '') {
                        setDepositTechnocoins('');
                        return;
                      }
                      if (/^\d*$/.test(next)) {
                        setDepositTechnocoins(next);
                      }
                    }}
                    placeholder="Технокоины"
                    type="text"
                    inputMode="numeric"
                    maxLength={MAX_TECHNOCOINS_DIGITS}
                    baseAddon={false}
                  />
                ) : (
                  <DateInput
                    className={s.dateInput}
                    value={expiresAtMask}
                    onChange={(e) => {
                      const next = typeof e === 'string' ? e : e.target.value;
                      setExpiresAtMask(next);
                    }}
                    placeholder="__.__.____"
                    error={''}
                    fullWidth
                  />
                )}

                <Input
                  className={s.inputField}
                  label=""
                  error={''}
                  value={depositRubles}
                  onChange={(e) => {
                    const next = typeof e === 'string' ? e : e.target.value;
                    if (next === '') {
                      setDepositRubles('');
                      return;
                    }
                    if (/^\d*$/.test(next)) {
                      setDepositRubles(next);
                    }
                  }}
                  placeholder="Сумма в рублях (необязательно)"
                  type="text"
                  inputMode="numeric"
                  baseAddon={false}
                />

                <Input
                  className={s.inputField}
                  label=""
                  error={''}
                  value={depositReason}
                  onChange={(e) => {
                    const next = typeof e === 'string' ? e : e.target.value;
                    setDepositReason(next);
                  }}
                  placeholder="Причина"
                  type="text"
                  baseAddon={false}
                />

                <Button
                  className={s.button}
                  disabled={!canDeposit || depositing}
                  onClick={() => {
                    void onDeposit();
                  }}
                >
                  Пополнить
                </Button>
              </div>
            )}

            <div className={classNames(s.sectionTitle, s.sectionTitleBeforeHistory)}>История операций</div>

            {(isLoading || (isFetching && operations.length === 0)) && (
              <div className={s.loaderWrapper}>
                <Loader />
              </div>
            )}

            {!isLoading && operations.length === 0 && <div className={s.emptyWrapper}>Операций не найдено</div>}

            {operations.length > 0 && (
              <div className={s.container}>
                <ul className={s.list}>
                  {operations.map((op) => (
                    <li key={op.id} className={s.item}>
                      <div className={s.itemMain}>
                        <div className={s.itemTitle}>
                          {operationTitle(op)} · {op.reason}
                        </div>
                        <div className={s.itemSub}>
                          {formatDateTimeForDisplay(op.createdAt)}
                          {op.editor?.fullName || op.editorId ? (
                            <> · {op.editor?.fullName ?? `#${op.editorId}`}</>
                          ) : null}
                        </div>
                      </div>
                      <div className={s.itemRight}>
                        <div>
                          <strong>
                            {op.type === 'deposit' ? '+' : '-'}
                            {formatTechnocoins(op.technocoins)} Ŧ
                          </strong>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {tab === 'usage' && (
          <>
            {!isReadOnly && (
              <div className={classNames(s.sectionTitle, s.sectionTitleCentered)}>Списать технокоины</div>
            )}

            {!isReadOnly && (
              <div className={s.formRow}>
                <Input
                  className={s.inputField}
                  label=""
                  error={withdrawErrorText}
                  value={withdrawTechnocoins}
                  onChange={(e) => {
                    const next = typeof e === 'string' ? e : e.target.value;
                    if (next === '') {
                      setWithdrawTechnocoins('');
                      return;
                    }
                    if (/^\d*$/.test(next)) {
                      setWithdrawTechnocoins(next);
                    }
                  }}
                  placeholder="Технокоины"
                  type="text"
                  inputMode="numeric"
                  maxLength={MAX_TECHNOCOINS_DIGITS}
                  baseAddon={false}
                />

                <Input
                  label=""
                  error={''}
                  value={withdrawReason}
                  onChange={(e) => {
                    const next = typeof e === 'string' ? e : e.target.value;
                    setWithdrawReason(next);
                  }}
                  placeholder="Причина"
                  type="text"
                  baseAddon={false}
                />

                <Button
                  className={s.button}
                  disabled={!canWithdraw || withdrawing}
                  onClick={() => {
                    void onWithdraw();
                  }}
                >
                  Списать
                </Button>
              </div>
            )}

            {(isLoading || (isFetching && usageItems.length === 0)) && (
              <div className={s.loaderWrapper}>
                <Loader />
              </div>
            )}

            {!isLoading && usageItems.length === 0 && <div className={s.emptyWrapper}>Списаний не найдено</div>}

            {usageItems.length > 0 && (
              <div className={s.container}>
                <ul className={s.usageList}>
                  {usageItems.map((u) => (
                    <li key={u.id} className={s.usageItem}>
                      <div className={s.usageColLeft}>
                        <div className={s.usageTitleRow}>
                          <span className={s.usagePrimary}>
                            {u.type === 'server' ? u.name || '—' : u.creatorLogin || '—'}
                          </span>
                        </div>
                        {u.type === 'server' && u.creatorLogin && (
                          <span className={s.usagePrimary}>{u.creatorLogin}</span>
                        )}
                        {u.type === 'training' && u.userLogin && <span className={s.usagePrimary}>{u.userLogin}</span>}
                        <span className={s.usageSecondary}>
                          {u.createdAt ? formatDateTimeForDisplay(u.createdAt) : '—'}
                        </span>
                        {u.closedAt ? (
                          <span className={s.usageSecondary}>Закрыт: {formatDateTimeForDisplay(u.closedAt)}</span>
                        ) : null}
                      </div>

                      <div className={s.usageColRight}>
                        <span className={s.usageRightPrimary}>
                          {u.type === 'server' ? formatPrivateId(u.privateServerId) : 'Тренировка'}
                        </span>
                        {u.type === 'server' && u.ip && u.port && (
                          <span className={s.usageRightInfo}>
                            {u.ip}:{u.port}
                          </span>
                        )}
                        <div className={s.spentTooltipContainer}>
                          <span className={s.usageSecondary}>Списано: {formatTechnocoins(u.technocoinsSpent)} Ŧ</span>
                          {u.roles && Object.keys(u.roles).length > 0 && (
                            <div className={s.spentTooltip}>
                              {Object.entries(u.roles).map(([roleKey, data]) => (
                                <div key={roleKey} className={s.spentTooltipRow}>
                                  {CLUB_ROLE_LABELS[roleKey as ClubRole] || roleKey}: {data.count} (
                                  {formatTechnocoins(data.technocoinsSpent)} Ŧ)
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {hasMoreUsage && (
                  <Button
                    className={s.button}
                    variant="outline"
                    disabled={usageFetching}
                    onClick={() => {
                      setUsageOffset((prev) => prev + PAGE_SIZE);
                    }}
                  >
                    {usageFetching ? 'Загрузка...' : 'Показать ещё'}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
